import { Form, Formik } from 'formik';
import { memo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../../../components/Card';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { useDeviceSize } from '../../../hooks/useDeviceSize';
import { T, useT } from '../../../translation/src';
import { useOrganisationBillingQuery } from '../../../types/graphqlTypes';
import { BillingFormValues, BillingInfoFields, useBillingInfoForm } from '../BillingInfoForm';

const useTranslations = () => {
  const billingDetails = useT('billing details', { swc: true });
  const save = useT('save', { swc: true });
  const saved = useT('billing details saved', { swc: true });
  return { billingDetails, save, saved };
};

// The organisation's billing details, as they go on its invoices. Nothing where
// billing is not set up (no Odoo): there is nowhere to keep them.
const OrganisationBillingInfo_: React.FC2 = () => {
  const translations = useTranslations();
  const { organisationId = '' } = useParams();
  const { xsDevice } = useDeviceSize();
  const { showToast } = useToastNotifications();
  const { data, error } = useOrganisationBillingQuery({ variables: { organisationId }, skip: !organisationId, fetchPolicy: 'cache-and-network' });
  // A failed lookup leaves the last result in place, which may be outdated: the card says so instead.
  const billing = error ? undefined : data?.organisationBilling;
  const onSaved = useCallback(() => showToast({ title: translations.saved, text: '', type: ToastType.SUCCESS }), [showToast, translations.saved]);
  const { formik, saving } = useBillingInfoForm({ organisationId, info: billing?.info, onSaved });

  if (billing && !billing.enabled) return null;

  return (
    <div className="flex space-x-4">
      <Formik<BillingFormValues> {...formik}>
        <Form className={xsDevice ? 'w-full' : 'w-1/2'}>
          {/* No overflow clipping: the country list drops out of the card. */}
          <Card title={translations.billingDetails} className="w-full" noOverflow {...(billing && { submitButtonTitle: translations.save, isSubmitting: saving })}>
            {billing ? (
              <BillingInfoFields />
            ) : (
              <span className="text-sm text-gray-500">{error ? <T _str="The billing details are not available right now. Try again later." /> : <T _str="loading" swc />}</span>
            )}
          </Card>
        </Form>
      </Formik>
    </div>
  );
};

export const OrganisationBillingInfo = memo(OrganisationBillingInfo_);
